import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { ArrowRight, HardHat, LockKeyhole, ShieldCheck, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext.jsx';
import { useApi } from '../lib/hooks.js';
import { Button, Card, Field, Input, useToast } from '../components/ui.jsx';
import BrandMark from '../components/BrandMark.jsx';

export default function Login() {
  const { actor, login } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { data: stats } = useApi('/public/stats');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (actor) return <Navigate to={actor.role === 'client' ? '/portal' : '/app'} replace />;

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const who = await login(email.trim(), password);
      toast.ok(`Welcome back, ${String(who.name).split(' ')[0]}`);
      navigate(who.role === 'client' ? '/portal' : '/app');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-split">
      <div className="login-form-side">
        <div className="login-form-inner anim-up">
          <Link to="/" className="row gap-12 mb-28">
            <BrandMark size={40} />
            <span className="brand-text">
              <strong>3Lumpers</strong>
              <span>Control panel</span>
            </span>
          </Link>

          <h1 style={{ fontSize: 'var(--fs-3xl)' }}>Sign in</h1>
          <p className="muted mt-8 mb-28">Every job, check-in, pallet count, pay run and invoice in one place.</p>

          <form onSubmit={submit} className="col gap-16">
            <Field label="Work email">
              <Input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@3lumpers.com"
                required
              />
            </Field>
            <Field label="Password">
              <Input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
            </Field>

            {error ? (
              <div className="field-error" style={{ fontSize: 'var(--fs-sm)' }}>{error}</div>
            ) : null}

            <Button type="submit" variant="primary" size="lg" loading={busy} trailing={<ArrowRight size={15} />}>
              Sign in to dispatch
            </Button>
          </form>


          <p className="dim mt-28" style={{ fontSize: 'var(--fs-sm)' }}>
            Lumper looking for work? <Link to="/signup?path=lumper" className="amber">Sign up here →</Link>
            <br />
            Need a crew at your dock? <Link to="/signup?path=business" className="amber">Sign your business up →</Link>
          </p>
        </div>
      </div>

      <aside className="login-aside">
        <div className="login-aside-inner anim-up">
          <div className="eyebrow">
            <span className="pip"><HardHat size={12} strokeWidth={2.4} /></span>
            Built for lumper operations
          </div>
          <h2 style={{ fontSize: 'var(--fs-4xl)', letterSpacing: '-0.035em', marginTop: 20 }}>
            Post the work.<br />Fill it in minutes.<br />Pay everybody correctly.
          </h2>
          <p className="muted mt-16" style={{ fontSize: 'var(--fs-lg)', maxWidth: '48ch' }}>
            Every job, dock check-in, pallet count, pay run and invoice on one screen,
            with the receipt to prove it.
          </p>

          <div className="col gap-12 mt-28">
            {[
              [Zap, 'Send a job to every available lumper nearby in one tap'],
              [ShieldCheck, 'Geofenced check-in, photo evidence and signed BOLs on every job'],
              [LockKeyhole, 'Pay and customer bills that match the pallet count'],
            ].map(([Icon, text]) => (
              <div key={text} className="row-top gap-12">
                <span className="feature-icon" style={{ width: 32, height: 32, borderRadius: 9, marginBottom: 0 }}>
                  <Icon size={15} strokeWidth={2} />
                </span>
                <span className="muted" style={{ paddingTop: 5 }}>{text}</span>
              </div>
            ))}
          </div>

          <Card flat className="mt-28" style={{ background: 'rgba(0,0,0,.32)' }}>
            <div className="card-body">
              <div className="hazard-rule mb-12" />
              <div className="row between">
                <div>
                  <div className="label">Avg. time to fill</div>
                  <div className="stat-value sm" style={{ fontSize: 'var(--fs-3xl)' }}>
                    {stats?.avgFillMinutes ?? '—'}<span className="unit">min</span>
                  </div>
                </div>
                <div className="right">
                  <div className="label">Jobs filled · 30d</div>
                  <div className="stat-value sm" style={{ fontSize: 'var(--fs-3xl)', color: 'var(--green)' }}>
                    {stats?.fillRate ?? '—'}<span className="unit">%</span>
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </aside>
    </div>
  );
}
